/**
 * Resolução de rótulo de canal — ponto ÚNICO usado pelo gráfico do chat e pela tela de
 * log/mapa (antes cada componente tinha sua própria cópia de `labelFor`, ambas caindo no
 * identificador técnico cru).
 *
 * Resolve dois problemas:
 *
 * 1. **Sufixo de dedup do PowerFT.** O backend deduplica canais repetidos com `_2`/`_3`
 *    (`RPMIndex_2`), e essas variantes NÃO existem no dicionário de rótulos. Aqui o sufixo é
 *    descascado — mas só quando o nome-base é um canal PowerFT conhecido, para não quebrar
 *    canais nativos legítimos que já terminam em número (`Generic_2`, `Custom_3`,
 *    `ExtIgnPoint_2`), que têm entrada própria no dicionário.
 *
 * 2. **Fallback.** Quando não há tradução, `resolved: false` avisa o chamador. Cada tela
 *    decide o que fazer: o gráfico do chat usa `label` (rótulo humano, sem vazar o nome
 *    técnico); a tela de log/mapa mantém o nome técnico visível de propósito, porque é por
 *    ele que o usuário procura o canal no seletor (ver `labelFor` em log-channels).
 *
 * i18n: os textos fixos daqui estão só em pt-br por decisão de escopo; en-us/es-es ficam
 * para depois (o dicionário de canais também tem 200 entradas sem es-es).
 */

import { POWERFT_CHANNEL_NAMES } from './powerft-channels.model';
import { VISION_FT_CHANNEL_LABELS } from './vision-ft-channel-labels';

export type ChannelLocale = 'pt' | 'en' | 'es';

/**
 * Rótulo exibido quando o canal não tem tradução no dicionário.
 *
 * Fica aqui (e não no transloco) porque este módulo é função PURA, sem injeção de
 * dependência — e o locale já é parâmetro. Mesmo padrão do dicionário de canais, que também
 * é indexado por idioma.
 */
const UNKNOWN_CHANNEL_LABELS: Record<ChannelLocale, string> = {
  pt: 'Canal não identificado',
  en: 'Unidentified channel',
  es: 'Canal no identificado',
};

/** Rótulo de canal desconhecido no idioma informado. */
export function unknownChannelLabel(locale: ChannelLocale = 'pt'): string {
  return UNKNOWN_CHANNEL_LABELS[locale] ?? UNKNOWN_CHANNEL_LABELS.pt;
}

const POWERFT_NAMES = new Set<string>(POWERFT_CHANNEL_NAMES);

/** Sufixo de deduplicação do backend: nome + "_" + ordinal. */
const DEDUP_SUFFIX = /^(.+)_(\d+)$/;

export interface ResolvedChannelLabel {
  /** Rótulo traduzido, ou `unknownChannelLabel(locale)` quando não há tradução. */
  label: string;
  /** Nome técnico exatamente como veio do backend. */
  raw: string;
  /** false = caiu no rótulo genérico (não há tradução para este canal). */
  resolved: boolean;
  /** Ordinal do dedup (`RPMIndex_2` → 2); null quando o nome não é deduplicado. */
  occurrence: number | null;
}

/**
 * Nome-base do canal, sem o sufixo de dedup — para casar com conjuntos de canais
 * (ex.: o Set de canais de RPM) sem que `RPMIndex_2` deixe de ser reconhecido como RPM.
 */
export function baseChannelName(channel: string): string {
  if (VISION_FT_CHANNEL_LABELS[channel]) return channel; // nome nativo tem precedência
  const m = DEDUP_SUFFIX.exec(channel);
  return m && POWERFT_NAMES.has(m[1]) ? m[1] : channel;
}

/** Resolve o rótulo de um canal. Nunca lança; nunca inventa tradução. */
export function resolveChannelLabel(
  channel: string | null | undefined,
  locale: ChannelLocale = 'pt',
): ResolvedChannelLabel {
  const raw = channel ?? '';
  if (!raw) {
    return { label: unknownChannelLabel(locale), raw: '', resolved: false, occurrence: null };
  }

  // Chave exata primeiro: canais nativos terminados em número (Generic_2) têm entrada própria
  // e não devem ser tratados como dedup.
  let key = raw;
  let occurrence: number | null = null;
  if (!VISION_FT_CHANNEL_LABELS[raw]) {
    const m = DEDUP_SUFFIX.exec(raw);
    if (m && POWERFT_NAMES.has(m[1])) {
      key = m[1];
      occurrence = Number(m[2]);
    }
  }

  const entry = VISION_FT_CHANNEL_LABELS[key];
  const translated = entry ? entry[locale] ?? entry.pt ?? entry.en ?? entry.es : undefined;
  if (!translated) {
    return { label: unknownChannelLabel(locale), raw, resolved: false, occurrence };
  }

  return {
    label: occurrence != null ? `${translated} (${occurrence})` : translated,
    raw,
    resolved: true,
    occurrence,
  };
}

/** Locale de canal derivado do idioma salvo na sessão. */
export function currentChannelLocale(): ChannelLocale {
  const raw = (sessionStorage.getItem('language') ?? '').toLowerCase();
  if (raw.startsWith('en')) return 'en';
  if (raw.startsWith('es')) return 'es';
  return 'pt';
}
